import logo from '@/assets/images/logo.svg';
import { ReactComponent as DocIcon } from '@/assets/images/svg/doc.svg';
import {
    BRIDGE,
    isBSCNetwork,
    isLazaiNetwork,
    ShowSelectNetwork,
} from '@/constant';
import { NavConfig } from '@/routes';
import application from '@/stores/application';
import user from '@/stores/user';
import { TYPE } from '@/theme';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Drawer, IconButton, Button as MButton } from '@mui/material';
import { observer, useObserver } from 'mobx-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import BSCSocialMedia from '../BSCSocialMedia';
import Button from '../Button';
import NetworkSelector from '../NetworkSelector';
import SelectLng from '../SelectLng';
import DailyBoxDropButton from './DailyBoxDropButton';
import PointsWrapper from './PointsWrapper';
import ConnectWalletButton from './connectWallet';

const HeaderWapper = styled.div`
    position: fixed;
    z-index: 88;
    left: 0;
    top: 0;
    width: 100%;
    height: 80px;
    background-color: white;
    display: flex;
    align-items: center;
    padding: 0 40px;

    .logo {
        height: 40px;
        cursor: pointer;

        ${({ theme }) => theme.mediaWidth.upToSmall`
            height: 30px;
        `}
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
        padding: 0 20px;
        height: 60px;
    `}
`;

const NavWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 30px;
    margin-left: 80px;
    margin-right: auto;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        display: none;
    `}
`;

const MobileMenuButton = styled(IconButton)`
    display: none !important;
    margin-left: auto;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        display: flex !important;
    `}
`;

const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #ebeffe;
`;

const DrawerContent = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const DrawerNavWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 30px;
`;

const NavButton = styled(MButton)<{ active: boolean }>`
    justify-content: flex-start;
    padding: 24px;
    width: 100%;
    border-radius: 12px;
    text-transform: none;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.15s ease-in-out;
    background-color: ${(props) => (props.active ? '#eff6ff' : 'transparent')};
    color: ${(props) => (props.active ? '#3563F3' : 'black')};

    &:hover {
        background-color: ${(props) => (props.active ? '#dbeafe' : '#f9fafb')};
    }

    & .MuiButton-startIcon {
        color: ${(props) => (props.active ? '#3563F3' : 'black')};
    }
`;

const DrawerTop = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
`;

const DrawerBottom = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;
    background-image: url(${require('@/assets/images/bridgebg.png')});
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
    padding: 20px;
    &.bsc-social-media {
        background-image: none;
    }

    .icon {
        width: 60px;
        height: 60px;
    }
`;

const Nav = styled(NavLink)`
    color: black;
    text-decoration: none;
    position: relative;

    &.active {
        font-weight: 600;
    }

    div {
        font-weight: inherit;
        font-size: 12px;

        &:hover {
            font-weight: 600;
        }

        &::after {
            content: attr(data-text);
            display: block;
            font-weight: 600;
            height: 0;
            overflow: hidden;
            visibility: hidden;
        }
    }
`;

const WalletWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;

    .pt-wrapper {
        border: 1px solid #ebeffe;
        border-radius: 12px;
        height: 40px;
        padding: 0 20px;
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;

        img {
            width: 20px;
            border-radius: 10px;
            overflow: hidden;
        }

        div {
            font-size: 14px;
        }
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
        display: none;
    `}
`;

const DrawerWalletAndPointWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;

    .pt-wrapper {
        border: 1px solid #ebeffe;
        border-radius: 12px;
        height: 40px;
        padding: 0 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        cursor: pointer;

        img {
            width: 20px;
            border-radius: 10px;
            overflow: hidden;
        }

        div {
            font-size: 14px;
        }
    }
`;

const StyledFaucet = styled(Button)`
    width: 100% !important;
    max-width: 160px;
    height: 38px !important;
`;

const StyledDoc = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid #ebeffe;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
    cursor: pointer;

    ${({ theme }) => theme.mediaWidth.upToSmall`
    margin-right: 0;
    margin-left: 20px;
    `};
    svg {
        color: #3563f3;
        width: 20px;
        height: 20px;
    }
`;

const BridgeButton = styled(Button)`
    width: 100% !important;
    max-width: unset;
`;

interface Props {
    showConnectButton?: boolean;
    connected?: boolean;
    onClickConnect?(): void;
    onClickDisconnect?(): void;
    address?: string;
    showLanguage?: boolean;
    notSupportedChain?: boolean;
}

function Header(props: Props) {
    const { t } = useTranslation();
    const { showLanguage } = props;
    const {
        connected: userConnected,
        userId,
        address: walletAddress,
        userDetail,
    } = user;

    const [drawerOpen, setDrawerOpen] = useState(false);
    const history = useHistory();
    const location = useLocation();

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    const Navs = () => (
        <NavWrapper>
            {/* <Nav
                id="premium"
                to="/"
                className={(isActive) => (isActive ? 'active' : '')}
                exact
            >
                <TYPE.main data-text={t('premium_launch')}>
                    {t('premium_launch')}
                </TYPE.main>
            </Nav> */}
        </NavWrapper>
    );

    const DrawerNavs = () => (
        <DrawerNavWrapper>
            {/* <Nav
                id="premium-mobile"
                to="/"
                className={(isActive) => (isActive ? 'active' : '')}
                exact
                onClick={() => setDrawerOpen(false)}
            >
                <TYPE.main data-text={t('premium_launch')}>
                    {t('premium_launch')}
                </TYPE.main>
            </Nav> */}
        </DrawerNavWrapper>
    );

    return useObserver(() => (
        <>
            <HeaderWapper>
                <img
                    className="logo"
                    src={logo}
                    alt="Lazpad logo"
                    onClick={() => {
                        history.push('/');
                    }}
                />
                {!application.comingSoon && (
                    <>
                        <Navs />
                        <StyledDoc
                            onClick={() => {
                                window.open(
                                    'https://lazbubu-1.gitbook.io/lazbubu/',
                                );
                            }}
                        >
                            <DocIcon />
                        </StyledDoc>
                        {isLazaiNetwork ? (
                            <DailyBoxDropButton
                                onClick={() => history.push('/lazbubu/boxdrop')}
                            />
                        ) : null}
                        <WalletWrapper>
                            {userId && <PointsWrapper />}
                            {showLanguage && <SelectLng />}
                            {ShowSelectNetwork && <NetworkSelector />}
                            <ConnectWalletButton
                                connected={userConnected}
                                walletAddress={walletAddress || ''}
                            />
                        </WalletWrapper>
                        {location.pathname.indexOf('/agent/') < 0 && (
                            <MobileMenuButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="end"
                                onClick={handleDrawerToggle}
                            >
                                <MenuIcon />
                            </MobileMenuButton>
                        )}
                    </>
                )}
            </HeaderWapper>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '80%',
                        maxWidth: '300px',
                        boxSizing: 'border-box',
                    },
                }}
            >
                <DrawerHeader>
                    <TYPE.main fontWeight={600}>Menu</TYPE.main>
                    <IconButton onClick={handleDrawerToggle}>
                        <CloseIcon />
                    </IconButton>
                </DrawerHeader>
                <DrawerContent>
                    {!application.comingSoon && (
                        <>
                            {/* <DrawerNavs /> */}
                            <DrawerWalletAndPointWrapper>
                                <DrawerTop>
                                    {NavConfig.map((item) => {
                                        const IconComponent = item.icon;
                                        const active = isActive(item.path);

                                        return (
                                            <NavButton
                                                key={item.label}
                                                active={active}
                                                onClick={() =>
                                                    history.push(item.path)
                                                }
                                                startIcon={<IconComponent />}
                                            >
                                                {item.label}
                                            </NavButton>
                                        );
                                    })}
                                </DrawerTop>

                                <DrawerBottom>
                                    {isLazaiNetwork && (
                                        <DrawerBottom>
                                            <img
                                                className="icon"
                                                src={require('@/assets/images/metis.svg')}
                                                alt=""
                                            />
                                            <TYPE.main
                                                fontSize={14}
                                                textAlign={'center'}
                                            >
                                                Get $METIS and Bridge to LazAI
                                                Alpha Mainnet
                                            </TYPE.main>
                                            <BridgeButton
                                                type="outline"
                                                size="small"
                                                onClick={() => {
                                                    window.open(BRIDGE);
                                                }}
                                            >
                                                Bridge Now!
                                            </BridgeButton>
                                        </DrawerBottom>
                                    )}
                                    {userId && drawerOpen && (
                                        <>
                                            <PointsWrapper />
                                            {/* <div
                                                className="pt-wrapper"
                                                onClick={() => {
                                                    window.open(
                                                        'https://lazpad.fun/dashboard',
                                                        '_blank',
                                                    );
                                                    setDrawerOpen(false);
                                                }}
                                            >
                                                <img
                                                    src={require('@/assets/images/bindbox.png')}
                                                    alt="bindbox icon"
                                                />
                                                <TYPE.main>
                                                    Activation Code:{' '}
                                                    {
                                                        user.userActivationCodes
                                                            ?.length
                                                    }
                                                </TYPE.main>
                                            </div> */}
                                        </>
                                    )}
                                    {ShowSelectNetwork && <NetworkSelector />}

                                    <ConnectWalletButton
                                        connected={userConnected}
                                        walletAddress={walletAddress || ''}
                                    />
                                    {showLanguage && <SelectLng />}
                                    {isBSCNetwork && (
                                        <DrawerBottom className="bsc-social-media">
                                            <BSCSocialMedia />
                                        </DrawerBottom>
                                    )}
                                </DrawerBottom>
                            </DrawerWalletAndPointWrapper>
                        </>
                    )}
                </DrawerContent>
            </Drawer>
        </>
    ));
}

export default observer(Header);
